import moment from 'moment';

type yearCalendar = {
	view: string,
	months: Array<Array<{ view: string, momentObject: moment.Moment, check: boolean, active: boolean }>>
}

type monthCalendar = {
	name: string,
	year: string,
	days: Array<any>,
	momentObject: moment.Moment,
	checked: boolean
}

export class Calendar {
    minDate: moment.Moment;
    maxDate: moment.Moment;
    start: moment.Moment;
    end: moment.Moment;
    display: Array<monthCalendar>;
    displayMonths: Array<yearCalendar>;
    checkMonths: any[] = [];

    static validateDateParam(date: Date | moment.Moment | string): any {
        if (moment.isMoment(date)) return date;
        if (moment.isDate(date)) return moment(date);
        if (typeof date == 'string') return moment(date); // Moment(date, this.format)
        return moment();
    }

    constructor(params: any) {
        this.validateParams(params);
        this.displayMonths = this.generateYears();
        this.display = this.generateListMonths();
    }

    validateParams(params: any) {
        this.minDate = Calendar.validateDateParam(params.minDate);
        this.maxDate = Calendar.validateDateParam(params.maxDate);
        this.start = Calendar.validateDateParam(params.start);
        this.end = Calendar.validateDateParam(params.end);
    }

    checkOneDay(day: moment.Moment) {
        this.start = day;
        this.end = day;
        this.checkStartEndDays(this.start, this.start);
    }

    checkDay(day: moment.Moment) {
        if (this.start != null && this.end != null) {
            this.end = null;
            this.start = day.clone();
            return this.checkStartEndDays(this.start, this.start);
        } else if (this.end == null) {
            this.end = day.clone();
            if (this.end.isBefore(this.start)) {
                let startDate = this.start.clone();
                this.start = this.end.clone();
                this.end = startDate;
            }
            this.checkStartEndDays(this.start, this.end);
        }
    }

    checkRangeMinMax(start: moment.Moment, end: moment.Moment) {
        start = (start.isSameOrBefore(this.minDate))? this.minDate.clone(): start;
        end = (end.isSameOrAfter(this.maxDate))? this.maxDate.clone(): end;
        return { start: start, end: end };
    }

    checkMonth(day: moment.Moment, firstClick) {
        let {start, end} = this.checkRangeMinMax(day.clone().startOf('month'), day.clone().endOf('month'));

        if(!firstClick) {
            start = (start.isSameOrBefore(this.start))? start: this.start.clone();
            end = (end.isSameOrAfter(this.end))? end: this.end.clone();
        }
        return this.setRange(start, end);
        /*
        if (this.end != null) {

            return this.checkDay(day.startOf('month'))
        }
        if (this.start.isAfter(day)) {
            let start = this.start.clone();
            return this.setRange(day.startOf('month'), start.endOf('month'));
        }
        return this.checkDay(day.endOf('month'))
        */
    }

    setStartEnd(start: moment.Moment, end: moment.Moment) {
        this.start = start.clone();
        this.end = end.clone();
    }

    checkMinMaxDate(start: moment.Moment, end: moment.Moment) {
        if (start.isBefore(this.minDate) || end.isAfter(this.maxDate)) start = this.minDate.clone();
        if (start.isBefore(this.minDate) || end.isAfter(this.maxDate)) end = this.maxDate.clone();
        return { start: start, end: end };
    }

    setRange(start: moment.Moment, end: moment.Moment) {
        let values = this.checkMinMaxDate(start, end);
        start = values.start;
        end = values.end;
        this.setStartEnd(start, end);
        this.checkStartEndDays(start, end);
    }

    checkStartEndDays(start: moment.Moment, end: moment.Moment) {
        this.checkStartEndMonths(start, end);
        this.display.forEach((month: any) => {
            month.checked = month.momentObject.isBetween(start, end, 'month', '[]');
            month.days.forEach((week: any[]) => {
                week.forEach(day => {
                    day.check = !day.nameDay && day.momentObject.isBetween(start, end, null, '[]');
                })
            })
        });
    }

    checkStartEndMonths(start: moment.Moment, end: moment.Moment) {
        this.updateCheckMonths();
        this.displayMonths.forEach(year => {
            year.months.forEach((item: any) => {
                item.forEach(month => {
                    month.check = month.momentObject.isBetween(start, end, 'month', '[]');
                })
            })
        });
    }

    updateCheckMonths() {
        let currentMonth = this.start.clone(), end = (this.end == null) ? this.start.clone() : this.end.clone();
        this.checkMonths = [];
        while (currentMonth.isSameOrBefore(end, 'month')) {
            let momentObject = currentMonth.clone();
            this.checkMonths.push(momentObject);
            currentMonth.add(1, 'month');
        }
    }

    generateYears() {
        let minYear = this.minDate.clone().startOf('year'),
            maxYear = this.maxDate.clone().startOf('year'),
            currentYear = minYear.clone(),
            listYears = [];
        while (currentYear.isSameOrBefore(maxYear)) {
            listYears.push({view: currentYear.format('YYYY'), months: this.generateMonthsInYear(currentYear)});
            currentYear.add(1, 'year');
        }
        return listYears;
    }

    generateMonthsInYear(year: moment.Moment) {
        let minMonth = year.clone().startOf('year'),
            maxMonth = year.clone().endOf('year'),
            currentMonth = minMonth.clone(),
            listMonths = [];
        while (currentMonth.isBetween(minMonth, maxMonth, 'month', '[]')) {
            let view = currentMonth.format('MMM'),
                momentObject = currentMonth.clone(),
                check = currentMonth.isBetween(this.start, this.end, 'month', '[]'),
                active = momentObject.isBetween(this.minDate, this.maxDate, 'month', '[]');
            if (check) this.checkMonths.push(momentObject);
            listMonths.push({view: view, momentObject: momentObject, check: check, active: active});
            currentMonth.add(1, 'month');
        }
        return [listMonths.slice(0, 6), listMonths.slice(6, 12)];
    }

    generateListMonths(): any[] {
        let listMonth = [], currentMonth = this.minDate.clone();
        while (currentMonth.isSameOrBefore(this.maxDate, 'month')) {
            listMonth.push({
                name: currentMonth.format('MMMM'),
                year: currentMonth.format('YYYY'),
                days: this.generateMonth(currentMonth),
                momentObject: currentMonth.clone(),
                checked: this.checkMonths.filter(i => i.isSame(currentMonth, 'month')).length > 0
            });
            currentMonth = currentMonth.add(1, 'month');
        }
        return listMonth;
    }

    generateMonth(day: any): any[][] {
        let monthStart = day.clone().startOf('month'),
            monthEnd = day.clone().endOf('month'),
            firstDay = monthStart.clone().startOf('week'),
            lastDay = monthEnd.clone().endOf('week'),
            currentDate = firstDay.clone(),
            listWeeks = [];
        while (currentDate.isSameOrBefore(lastDay.startOf('week'))) {
            listWeeks.push(this.generateWeek(currentDate, day.month()));
            currentDate.add(1, 'week');
        }
        return listWeeks;
    }

    generateWeek(day: any, monthNumber: number) {
        let firstDayWeek = day.clone().startOf('week'),
            lastDayWeek = day.clone().endOf('week'),
            currentDay = day.clone().startOf('week').clone(),
            listDays = [];
        while (currentDay.isBetween(firstDayWeek, lastDayWeek, 'day', '[]')) {
            listDays.push(this.generateDay(currentDay, monthNumber));
            currentDay.add(1, 'day');
        }
        return listDays;
    }

    generateDay(date: moment.Moment, monthNumber: number): any {
        let view = date.format('D'),
            momentObject = date.clone(),
            active = date.isBetween(this.minDate, this.maxDate, null, '[]') && date.month() == monthNumber,
            start = this.start,
            end = this.end || this.start,
            check = date.isBetween(start, end, null, '[]');
        return { view: view, momentObject: momentObject, active: active, check: check };
    }
}